import { Drawer, DrawerProps, Flex, CheckboxChangeEvent } from "antd";
import { StoryboardThumbnails } from "../StoryboardThumbnails";
import { Dispatch, SetStateAction } from "react";
import { useTranslate } from "@refinedev/core";
import { TabsCollapseButton } from "../controls/TabsCollapseButton";
import { ShadowButton } from "components/ShadowButton/";
import { useTemplateEditorStore } from "../../store/useTemplateEditorStore";
import { useTemplateData } from "../../hooks/useTemplateData";
import { TemplateType } from "../../types";
import { styled } from "styled-components";
import { StoryboardSlidesThumbnails } from "../StoryboardSlidesThumbnails";

export const ApplySettingsDrawer = ({
  title,
  onClose,
  onApplySettings,
  checkedChapters,
  setCheckedChapters,
  allSceneTypes = false,
  ...props
}: DrawerProps & {
  onApplySettings: (templateIds: string[]) => void;
  checkedChapters: string[];
  setCheckedChapters: Dispatch<SetStateAction<string[]>>;
  allSceneTypes?: boolean;
}) => {
  const {
    projectId,
    templatesData: { hasTransition, isFlashProject },
    storyboardSections,
  } = useTemplateData();

  const t = useTranslate();

  const { currentChapter, slideLayoutType } = useTemplateEditorStore(
    (store) => ({
      currentChapter: store.currentChapter,
      slideLayoutType: store.templateState.slideLayoutType,
    })
  );

  const handleChapterCheckboxChange = (
    e: CheckboxChangeEvent,
    templateId?: string
  ) => {
    if (e.target.checked) {
      if (templateId) {
        setCheckedChapters([...checkedChapters, templateId]);
      }
    } else {
      setCheckedChapters(checkedChapters.filter((id) => id !== templateId));
    }
  };

  if (!projectId) return null;

  return (
    <Drawer
      placement="right"
      width={400}
      styles={{
        wrapper: {
          height: "100%",
        },
        header: {
          borderBottom: "none",
        },
        footer: {
          borderTop: "none",
        },
        body: {
          overflow: "hidden",
          padding: 0,
        },
      }}
      {...props}
      onClose={onClose}
      closeIcon={null}
      footer={
        <Flex justify="center" align="center" style={{ padding: 16 }}>
          <ShadowButton
            type="primary"
            size="large"
            shape="round"
            onClick={() => {
              onApplySettings(checkedChapters);
            }}
          >
            {t("components.templates.settings.applySettings.cta")}
          </ShadowButton>
        </Flex>
      }
    >
      <div
        style={{
          position: "absolute",
          left: -16,
          top: 8,
          transform: "scaleX(-1)",
        }}
      >
        <TabsCollapseButton onClick={(e) => onClose?.(e)} />
      </div>
      <StyledScrollableFlex justify="center" align="center">
        {isFlashProject ? (
          <StoryboardSlidesThumbnails
            showCheckbox={true}
            checkboxProps={{
              checkedScenes: checkedChapters,
              handleChapterCheckboxChange,
            }}
            filterSlideLayoutType={allSceneTypes ? undefined : slideLayoutType}
          />
        ) : (
          <StoryboardThumbnails
            hasTransition={hasTransition}
            sections={storyboardSections}
            showCheckbox={true}
            checkedChapters={checkedChapters}
            handleChapterCheckboxChange={handleChapterCheckboxChange}
            visibilitySettings={{
              hideIntro: true,
              hideClosing: true,
              hideTransitions:
                !allSceneTypes && currentChapter.type === TemplateType.CHAPTER,
              hideChapters:
                !allSceneTypes &&
                currentChapter.type === TemplateType.TRANSITION,
            }}
          />
        )}
      </StyledScrollableFlex>
    </Drawer>
  );
};

const StyledScrollableFlex = styled(Flex)`
  height: 100%;
  overflow-y: auto;
  padding-right: 16px;
`;
